import { useEffect, useState } from "react";
import { useNavigate } from "react-router";
import type { WebinarSummary } from "../../shared/types";
import { boardPath } from "../board/paths";
import { api } from "../lib/api";
import { InviteSheet, inviteTargetOf } from "./InviteSheet";
import { Button } from "./ui/Button";
import { Card } from "./ui/Card";
import { EmptyState } from "./ui/EmptyState";
import type { IconName } from "./ui/Icon";
import { useToast } from "./ui/Toast";

type FirstRoomPlace = "rooms" | "people" | "conversations" | "automations";

/** The brand-new account's rooms: the first one made, or none yet; loaded here unless the screen already has them. */
function useFirstRoom(given: WebinarSummary | null | undefined): WebinarSummary | null | undefined {
  const [room, setRoom] = useState<WebinarSummary | null | undefined>(given);
  useEffect(() => {
    if (given !== undefined) return setRoom(given);
    let alive = true;
    api.webinars
      .list()
      .then((r) => alive && setRoom(r.webinars.find((w) => !w.quick_live) ?? null))
      .catch(() => alive && setRoom(null));
    return () => {
      alive = false;
    };
  }, [given]);
  return room;
}

function words(place: FirstRoomPlace, room: WebinarSummary | null): { icon: IconName; title: string; text?: string } {
  if (place === "rooms") return { icon: "play", title: "Make your first room" };
  if (place === "people") return { icon: "users", title: "No one yet" };
  if (place === "conversations") return { icon: "chat", title: "No conversations yet" };
  return room
    ? { icon: "zap", title: `Follow-ups for ${room.title} are ready`, text: "0 people in them so far" }
    : { icon: "zap", title: "Follow-ups come with each room" };
}

/**
 * What a list shows before a business has anyone in it (`v-fresh.js`): with no room yet, the two ways to make one;
 * with a draft, the way back to it; once it is published, its sign-up link and Invite people.
 */
export function FirstRoomEmpty({ place, room: given }: { place: FirstRoomPlace; room?: WebinarSummary | null }) {
  const navigate = useNavigate();
  const toast = useToast();
  const room = useFirstRoom(given);
  const [inviting, setInviting] = useState(false);
  if (room === undefined) return null;
  const w = words(place, room);
  const published = room?.status === "published";

  let actions;
  if (!room || place === "rooms") {
    actions = (
      <>
        <Button variant="p" icon="upload" onClick={() => navigate("/rooms/new?from=recording")}>
          From a recording
        </Button>
        <Button icon="live" onClick={() => navigate("/rooms/new?from=live")}>
          A live room
        </Button>
      </>
    );
  } else if (!published) {
    actions = (
      <Button variant="p" onClick={() => navigate(boardPath(room.id))}>
        Open the draft
      </Button>
    );
  } else {
    actions = (
      <>
        <Button
          variant="p"
          icon="link"
          onClick={() =>
            void navigator.clipboard
              ?.writeText(room.register_url)
              .then(() => toast({ text: `Copied: ${room.register_url}`, icon: "copy" }))
              .catch(() => undefined)
          }
        >
          Copy the sign-up link
        </Button>
        <Button icon="userplus" onClick={() => setInviting(true)}>
          Invite people
        </Button>
      </>
    );
  }

  return (
    <Card>
      <EmptyState icon={w.icon} title={w.title} text={w.text} actions={actions} />
      {inviting && room ? (
        <InviteSheet
          webinar={inviteTargetOf(room)}
          onClose={() => setInviting(false)}
          onSent={() => setInviting(false)}
          onToast={(m) => toast(m)}
        />
      ) : null}
    </Card>
  );
}
